import compact from 'lodash/compact';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import maxBy from 'lodash/maxBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import {
  CounterpartyIdentity,
  CounterpartyIdentityKeyType,
  CounterpartyIdentityKind,
  CounterpartyRoleTargetType,
  Transaction,
  TransactionExclusionReason,
  TransactionLink,
  TransactionLinkType,
} from '../types/model-app';

import {
  buildCounterpartyIdentityId,
  buildCounterpartyRoleId,
  CounterpartyIdentityKey,
  getCounterpartyRoleSubjectId,
  getTransactionIdentityKeys,
  isTextIdentity,
} from './counterpartyIdentity';
import {
  CANDIDATE_LEASE_NAME_MATCH_THRESHOLD,
  CandidateLeaseContact,
  getCounterpartNameMatchScore,
} from './transactionCandidateLeases';

const LEARNING_KEY_PRECEDENCE = [
  CounterpartyIdentityKeyType.IBAN,
  CounterpartyIdentityKeyType.MANDATE_ID,
  CounterpartyIdentityKeyType.CREDITOR_ID,
];

export interface LeasePayer {
  contactId: string;
  contact: CandidateLeaseContact;
}

export interface CounterpartyLearningExclusion {
  reason: TransactionExclusionReason;
  comment?: string | null;
}

export interface CounterpartyLearningInput {
  clientId: string;
  transaction: Pick<Transaction, 'id' | 'counterpartAccountNumber' | 'mandateId' | 'creditorId' | 'counterpartName'>;
  targets: Pick<TransactionLink, 'linkId' | 'linkType'>[];
  existingIdentity?: CounterpartyIdentity | null;
  /** Learns on this account, mandate or creditor rather than the transaction's preferred key */
  identityKey?: CounterpartyIdentityKey;
  getLeasePayers: (leaseId: string) => LeasePayer[];
  exclusion?: CounterpartyLearningExclusion;
}

export interface LearnedIdentity {
  id: string;
  keyType: CounterpartyIdentityKeyType;
  key: string;
  isFullRemittance?: boolean | null;
  kind: CounterpartyIdentityKind;
  contactId: string | null;
  exclusionReason: TransactionExclusionReason | null;
  exclusionComment: string | null;
  lastCounterpartName: string | null;
}

export interface LearnedRole {
  id: string;
  subjectId: string;
  targetType: CounterpartyRoleTargetType;
  targetId: string;
}

export interface CounterpartyLearningPlan {
  identity: LearnedIdentity;
  roles: LearnedRole[];
  /** A user linking a movement of an account the register excludes contradicts that exclusion */
  withdrawsExclusion: boolean;
}

const ROLE_TARGET_TYPE_BY_LINK_TYPE: Record<TransactionLinkType, CounterpartyRoleTargetType> = {
  [TransactionLinkType.LEASE]: CounterpartyRoleTargetType.LEASE,
  [TransactionLinkType.CONTACT]: CounterpartyRoleTargetType.SUPPLIER,
  [TransactionLinkType.OWNER]: CounterpartyRoleTargetType.OWNER,
};

// The holder of these kinds is that target itself: the resolver derives it without a role
const HOLDER_KIND_BY_LINK_TYPE: Partial<Record<TransactionLinkType, CounterpartyIdentityKind>> = {
  [TransactionLinkType.CONTACT]: CounterpartyIdentityKind.SUPPLIER,
  [TransactionLinkType.OWNER]: CounterpartyIdentityKind.OWNER_ACCOUNT,
};

export const getExcludedCounterpartyKind = (reason?: TransactionExclusionReason | null): CounterpartyIdentityKind =>
  reason === TransactionExclusionReason.INTERNAL_TRANSFER
    ? CounterpartyIdentityKind.INTERNAL_ACCOUNT
    : CounterpartyIdentityKind.OTHER;

/** The role a decision on this target reinforces, or null when the identity's holder is that target itself */
export const getLearnedRoleId = (
  identity: Pick<CounterpartyIdentity, 'id' | 'contactId' | 'kind'>,
  target: Pick<TransactionLink, 'linkId' | 'linkType'>
): string | null => {
  const subjectId = getCounterpartyRoleSubjectId(identity);
  if (target.linkId === subjectId && HOLDER_KIND_BY_LINK_TYPE[target.linkType] === identity.kind) {
    return null;
  }
  return buildCounterpartyRoleId(subjectId, ROLE_TARGET_TYPE_BY_LINK_TYPE[target.linkType], target.linkId);
};

const pickLearningKey = (
  transaction: CounterpartyLearningInput['transaction']
): CounterpartyIdentityKey | undefined => {
  const keys = getTransactionIdentityKeys(transaction);
  return compact(LEARNING_KEY_PRECEDENCE.map((keyType) => keys.find((key) => key.keyType === keyType)))[0];
};

const pickLeasePayer = (payers: LeasePayer[], counterpartName?: string | null): string | null => {
  const scored = payers.map((payer) => ({
    contactId: payer.contactId,
    score: getCounterpartNameMatchScore(counterpartName, [payer.contact]),
  }));
  const best = maxBy(scored, 'score');
  if (isNil(best) || best.score < CANDIDATE_LEASE_NAME_MATCH_THRESHOLD) {
    return null;
  }
  const bestContactIds = uniq(
    scored.reduce<string[]>((acc, { contactId, score }) => {
      if (score === best.score) {
        acc.push(contactId);
      }
      return acc;
    }, [])
  );
  return bestContactIds.length === 1 ? best.contactId : null;
};

const getHolder = (input: CounterpartyLearningInput): { contactId: string | null; kind: CounterpartyIdentityKind } => {
  const { exclusion, targets, transaction, getLeasePayers } = input;
  if (!isNil(exclusion)) {
    return { contactId: null, kind: getExcludedCounterpartyKind(exclusion.reason) };
  }
  const firstTarget = head(targets);
  switch (firstTarget?.linkType) {
    case TransactionLinkType.CONTACT:
      return { contactId: firstTarget.linkId, kind: CounterpartyIdentityKind.SUPPLIER };
    case TransactionLinkType.OWNER:
      return { contactId: firstTarget.linkId, kind: CounterpartyIdentityKind.OWNER_ACCOUNT };
    case TransactionLinkType.LEASE:
      return {
        contactId: pickLeasePayer(getLeasePayers(firstTarget.linkId), transaction.counterpartName),
        kind: CounterpartyIdentityKind.TENANT_PAYER,
      };
    default:
      return { contactId: null, kind: CounterpartyIdentityKind.UNKNOWN };
  }
};

/**
 * What a manual decision teaches the register: the account of the transaction, the contact behind it (the target
 * itself for a supplier or an owner, the best-named tenant or guarantor for a lease, nobody for a third party),
 * and one payer role per target. An identity already known keeps its holder: its roles stay where they hang. A
 * migrated text identity lends neither its holder nor its roles to the account a movement teaches in its place.
 */
export const planCounterpartyLearning = (input: CounterpartyLearningInput): CounterpartyLearningPlan | null => {
  const { clientId, transaction, targets, existingIdentity, identityKey, exclusion } = input;
  // The identity the engine matched (mandate, creditor, account, then a migrated text) is the one a decision confirms
  // or corrects. A movement that also carries an account teaches that account rather than the migrated text.
  const transactionKey = identityKey ?? pickLearningKey(transaction);
  const isKeyedOnExistingIdentity =
    !isNil(existingIdentity) && (!isTextIdentity(existingIdentity) || isNil(transactionKey));
  const learningKey = isKeyedOnExistingIdentity
    ? { keyType: existingIdentity.keyType, key: existingIdentity.key }
    : transactionKey;
  if (isNil(learningKey) || (isNil(exclusion) && isEmpty(targets))) {
    return null;
  }
  const id = isKeyedOnExistingIdentity
    ? existingIdentity.id
    : buildCounterpartyIdentityId(clientId, learningKey.keyType, learningKey.key);
  const holder = isKeyedOnExistingIdentity
    ? { contactId: existingIdentity.contactId ?? null, kind: existingIdentity.kind }
    : getHolder(input);
  const subjectId = getCounterpartyRoleSubjectId({ id, contactId: holder.contactId });
  const roles = isNil(exclusion)
    ? uniqBy(targets, (target) => `${target.linkType}#${target.linkId}`).reduce<LearnedRole[]>((acc, target) => {
        const roleId = getLearnedRoleId({ id, contactId: holder.contactId, kind: holder.kind }, target);
        if (isNil(roleId)) {
          return acc;
        }
        acc.push({
          id: roleId,
          subjectId,
          targetType: ROLE_TARGET_TYPE_BY_LINK_TYPE[target.linkType],
          targetId: target.linkId,
        });
        return acc;
      }, [])
    : [];
  return {
    identity: {
      id,
      keyType: learningKey.keyType,
      key: learningKey.key,
      isFullRemittance: isKeyedOnExistingIdentity ? existingIdentity.isFullRemittance : undefined,
      kind: holder.kind,
      contactId: holder.contactId,
      exclusionReason: exclusion?.reason ?? null,
      exclusionComment: exclusion?.comment ?? null,
      lastCounterpartName: transaction.counterpartName ?? null,
    },
    roles,
    withdrawsExclusion: isNil(exclusion) && !isNil(existingIdentity?.exclusionReason),
  };
};
